import { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { analytics } from '../services/analytics';

interface AnalyticsContextType {
  analytics: typeof analytics;
  isReady: boolean;
  trackEvent: typeof analytics.trackEvent;
  trackPageView: typeof analytics.trackPageView;
  trackAuth: typeof analytics.trackAuth;
  trackSubscription: typeof analytics.trackSubscription;
  trackStrategy: typeof analytics.trackStrategy;
  trackCompany: typeof analytics.trackCompany;
  trackExecution: typeof analytics.trackExecution;
  trackOrganization: typeof analytics.trackOrganization;
  trackReport: typeof analytics.trackReport;
  trackEngagement: typeof analytics.trackEngagement;
  trackError: typeof analytics.trackError;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  const auth = useAuth();
  const userHook = useUser();
  const isSignedIn = auth.isSignedIn ?? false;
  const isAuthLoaded = auth.isLoaded ?? true;
  const userId = auth.userId ?? null;
  const user = userHook.user ?? null;

  const [isAnalyticsReady, setIsAnalyticsReady] = useState(false);

  // Initialize analytics service when provider mounts
  useEffect(() => {
    let isMounted = true;

    const initializeAnalytics = async () => {
      try {
        await analytics.initialize();
        if (isMounted) {
          setIsAnalyticsReady(true);
        }
      } catch (error) {
        console.error('Analytics Provider: ❌ Failed to initialize analytics:', error);
      }
    };

    initializeAnalytics();

    return () => {
      isMounted = false;
    };
  }, []);

  // Track user authentication events
  useEffect(() => {
    if (isAuthLoaded && isSignedIn && user && isAnalyticsReady) {
      // Detect new signup: user created within last 2 minutes
      // This ensures CompleteRegistration fires client-side for Facebook conversion tracking
      const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
      const isNewSignup = createdAt > 0 && Date.now() - createdAt < 2 * 60 * 1000;
      analytics.trackAuth(isNewSignup ? 'sign_up' : 'sign_in');

      // Set user properties for better analytics
      analytics.setUserProperties({
        user_id: userId || user.id,
        email: user.emailAddresses[0]?.emailAddress || 'unknown',
        created_at: user.createdAt ? new Date(user.createdAt).toISOString() : 'unknown',
      });
    }
  }, [isAuthLoaded, isSignedIn, user, userId, isAnalyticsReady]);

  // Track user engagement - time on page
  useEffect(() => {
    if (!isAnalyticsReady) return;

    const startTime = Date.now();

    return () => {
      const timeOnPage = Date.now() - startTime;
      if (timeOnPage > 5000) {
        // Only track if user stayed more than 5 seconds
        analytics.trackEngagement('time_on_page', location.pathname, Math.floor(timeOnPage / 1000));
      }
    };
  }, [location, isAnalyticsReady]);

  // Track scroll depth
  useEffect(() => {
    if (!isAnalyticsReady) return;

    let maxScrollDepth = 0;
    const scrollDepthIntervals: Set<number> = new Set();

    const handleScroll = () => {
      const scrollDepth = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      if (scrollDepth > maxScrollDepth) {
        maxScrollDepth = scrollDepth;

        // Track scroll depth milestones
        const milestones = [25, 50, 75, 90, 100];
        milestones.forEach((milestone) => {
          if (scrollDepth >= milestone && !scrollDepthIntervals.has(milestone)) {
            scrollDepthIntervals.add(milestone);
            analytics.trackEngagement('scroll_depth', location.pathname, milestone);
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location, isAnalyticsReady]);

  // Track errors globally
  useEffect(() => {
    if (!isAnalyticsReady) return;

    const handleError = (event: ErrorEvent) => {
      analytics.trackError(`${event.message} at ${event.filename}:${event.lineno}`, true);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      analytics.trackError(`Unhandled promise rejection: ${event.reason}`, true);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isAnalyticsReady]);

  const contextValue: AnalyticsContextType = {
    analytics,
    isReady: isAnalyticsReady,
    trackEvent: analytics.trackEvent.bind(analytics),
    trackPageView: analytics.trackPageView.bind(analytics),
    trackAuth: analytics.trackAuth.bind(analytics),
    trackSubscription: analytics.trackSubscription.bind(analytics),
    trackStrategy: analytics.trackStrategy.bind(analytics),
    trackCompany: analytics.trackCompany.bind(analytics),
    trackExecution: analytics.trackExecution.bind(analytics),
    trackOrganization: analytics.trackOrganization.bind(analytics),
    trackReport: analytics.trackReport.bind(analytics),
    trackEngagement: analytics.trackEngagement.bind(analytics),
    trackError: analytics.trackError.bind(analytics),
  };

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}

// Export individual tracking functions for convenience
export const useTrackEvent = () => useAnalytics().trackEvent;
export const useTrackSubscription = () => useAnalytics().trackSubscription;
export const useTrackStrategy = () => useAnalytics().trackStrategy;
export const useTrackCompany = () => useAnalytics().trackCompany;
export const useTrackExecution = () => useAnalytics().trackExecution;
export const useTrackOrganization = () => useAnalytics().trackOrganization;
export const useTrackReport = () => useAnalytics().trackReport;
export const useTrackEngagement = () => useAnalytics().trackEngagement;
export const useTrackError = () => useAnalytics().trackError;
